import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { SeatStatus } from '../Classes';
import { Icon, IconName } from 'react-frontier';
import { SeatStyle } from '@arema/types/public';
import classNames from 'classnames';

const SEAT_WIDTH = 60;
const SEAT_HEIGHT = 40;
const SEAT_MARGIN = 2;

interface DefaultProps extends PropsWithChildren{
	style?: React.CSSProperties,
	className?: string,
}

interface SeatmapSeatProps extends DefaultProps{
	selected?: boolean,
	text?: string,
	subtext?: string,
	meta?: string,
	empty?: boolean,
	stage?: (0 | 1)[],
	size?: number,
	seatStyle?: SeatStyle,
	color?: 'green' | 'red' | 'purple' | 'black' | 'blue' | 'orange' | 'white' | 'yellow' | 'gray' | 'transparent' | 'light-red',
	textColor?: string,
	iconName?: IconName,
	status?: SeatStatus,
	statusIcon?: boolean,
	textStyle?: React.CSSProperties,
	onClick?: ()=>void,
}

const SeatmapSeat = React.forwardRef((props: SeatmapSeatProps, ref: any)=>{
	var radius : string[] = [];
	if(!!props.stage){
		if(props.stage[0]){
			radius.push("top-left");
		}
		if(props.stage[1]){
			radius.push("top-right");
		}
		if(props.stage[2]){
			radius.push("bottom-left");
		}
		if(props.stage[3]){
			radius.push("bottom-right");
		}
	}

	var color = props.color;
	if(!props.color && props.status){
		switch(props.status){
			case SeatStatus.FREE: color = 'gray';
			break;
			case SeatStatus.BLOCKED: color = 'red';
			break;
			case SeatStatus.HIDDEN: color = 'transparent';
			break;
			case SeatStatus.LOCKED: color = 'gray';
			break;
			case SeatStatus.FREE_PDV: color = 'purple';
			break;
		}
	}

	var getStatusIcon = () : IconName=>{
		switch(props.status){
			case SeatStatus.BLOCKED: return 'ban';
			case SeatStatus.LOCKED: return 'lock';
			case SeatStatus.FREE_PDV: return 'cash-register';
			case SeatStatus.HIDDEN: return 'eye-slash';
			default: null;
		}
	}
	
	var seat_icon = useMemo(()=>{
		return props.iconName || (props.statusIcon!==false ? getStatusIcon() : null);
	}, [props.iconName, props.statusIcon, props.status])


	var seat_style : SeatStyle = useMemo(()=>{
		return {
			width: props.seatStyle?.width || SEAT_WIDTH,
			height: props.seatStyle?.height || SEAT_HEIGHT,
			padding: props.seatStyle?.padding || SEAT_MARGIN,
			borderRadius: props.seatStyle?.borderRadius || undefined,
			text: (typeof props.seatStyle?.text)!=='undefined' ? props.seatStyle?.text : true,
			fontSize: props.seatStyle?.fontSize || undefined,
		}
	}, [props.seatStyle]);

	var seat_width = Math.max((seat_style.width || SEAT_WIDTH), ((seat_style.width || SEAT_WIDTH)*(props.size || 1)))

	return <div ref={ref} className={classNames('fr seatmap-seat', props.className, color, radius, {
		stage: !!props.stage,
		empty: props.empty,
		selected: !!props.selected,
	})} style={{
		width: seat_width,
		minWidth: seat_width,
		height: seat_style.height,
		...props.style,
	}} onClick={props.onClick}>
		{!!props.meta && props.meta.length>0 && (
			<div className="meta">{props.meta}</div>
		)}
		<div className="text" style={{
			fontSize: seat_style.fontSize,
			...props.textStyle,
		}}>
			{!!seat_icon && <Icon name={seat_icon} />}
			{props.text || props.children}
		</div>
		{!!props.subtext && props.subtext.length>0 && (
			<div className="subtext">{props.subtext}</div>
		)}
	</div>
})

interface SeatmapRowProps extends DefaultProps{
	seats?: SeatmapSeatProps[],
	seatAmount?: number,
	width?: number,
	seatStyle?: SeatStyle,
}

const SeatmapRow = (props: SeatmapRowProps)=>{
	return <div className={classNames("fr seatmap-row", props.className)} style={{
		...(props.seatAmount ? {
			width: ((props.seatStyle?.width || SEAT_WIDTH)*props.seatAmount)+(props.seatAmount*(props.seatStyle?.padding || SEAT_MARGIN)*2)-(props.seatStyle?.padding || SEAT_MARGIN)+20
		} : {}),
		...props.style
	}}>
		{props.children}
		{!!props.seats && props.seats.length>0 && props.seats.map((a, i)=>(
			<SeatmapSeat key={`RWST-${i}`} {...props} />
		))}
	</div>
}

type SeatmapSubComponents = {
	Row: typeof SeatmapRow,
	Seat: typeof SeatmapSeat,
}

interface SeatmapProps extends DefaultProps{
	initialScroll?: number,
	containerStyle?: React.CSSProperties
	dragScroll?: boolean,
}

const Seatmap : React.FC<SeatmapProps> & SeatmapSubComponents = (props: SeatmapProps)=>{
	var seatmapRef = useRef<HTMLDivElement>(null);
	var [drag, setDrag] = useState<{
		dragging: boolean,
		moved: boolean,
		start: { x: number, y: number },
		offset: { x: number, y: number },
	}>(null);

	useEffect(()=>{
		if(seatmapRef.current){
			seatmapRef.current.scroll({
				left: props.initialScroll-Math.floor(seatmapRef.current.clientWidth/2),
				top: props.initialScroll-Math.floor(seatmapRef.current.clientHeight/2),
			})
		}
	}, []);

	var onMouseDown = (e: React.MouseEvent<HTMLDivElement>)=>{
		setDrag({
			dragging: true,
			moved: false,
			start: {
				x: e.pageX-seatmapRef.current?.offsetLeft,
				y: e.pageY-seatmapRef.current?.offsetTop
			},
			offset: {
				x: seatmapRef.current?.scrollLeft,
				y: seatmapRef.current?.scrollTop,
			}
		})
	}
	var onMouseUp = ()=>{
		setDrag(d=>({ ...d, dragging: false }));
	}
	var onClickCapture = (e: React.MouseEvent<HTMLDivElement>)=>{
		if(drag && drag.moved){
			e.preventDefault();
			e.stopPropagation();
		}
	}
	var onMouseMove = (e: React.MouseEvent<HTMLDivElement>)=>{
		if(!drag?.dragging || !seatmapRef?.current) return;
		e.preventDefault();

		var movedX = (e.pageX - seatmapRef.current?.offsetLeft)-drag.start.x;
		var movedY = (e.pageY - seatmapRef.current?.offsetTop)-drag.start.y;

		if(Math.abs(movedX)>3 || Math.abs(movedY)>3){
			setDrag(p=>({ ...p, moved: true }));
		}

		seatmapRef.current.scrollLeft = drag.offset.x - movedX;
		seatmapRef.current.scrollTop = drag.offset.y - movedY;
	}
	
	return <div className={classNames("fr seatmap", props.className)} style={props.style}>
		<div 
			ref={seatmapRef} 
			className="seats" 
			style={props.containerStyle}
			onMouseMove={props.dragScroll ? onMouseMove : null}
			onMouseDown={props.dragScroll ? onMouseDown : null}
			onMouseUp={props.dragScroll ? onMouseUp : null}
			onMouseLeave={props.dragScroll ? onMouseUp : null}
			onClickCapture={props.dragScroll ? onClickCapture : null}
		>	
			{props.children}
		</div>
	</div>
}

Seatmap.Row = SeatmapRow;
Seatmap.Seat = SeatmapSeat;

export default Seatmap;